import { useState, useEffect, useRef } from "react";
import { Button, Modal, Row, Col } from "react-bootstrap";

const DATE_TYPE_OPTIONS = [
  { id: "lead_date", name: "Lead Date" },
  { id: "intake_date", name: "Intake Date" },
  { id: "retained_date", name: "Retained Date" },
  { id: "acceptance_date", name: "Acceptance Date" },
  { id: "closed_date", name: "Closed Date" },
  { id: "referred_date", name: "Referred Date" },
  { id: "rejected_date", name: "Rejected Date" },
];
const PRESET_FIELDS = [
  "current_day",
  "current_week",
  "current_month",
  "previous_month",
  "current_quarter",
  "previous_quarter",
];

const getDateRangeFromPreset = (preset) => {
  const today = new Date();
  let start = new Date();
  let end = new Date();

  switch (preset) {
    case "current_day":
      start = end = today;
      break;

    case "current_week": {
      const day = today.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start = new Date(today);
      start.setDate(today.getDate() + diff);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }

    case "current_month":
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;

    case "previous_month":
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;

    case "current_quarter": {
      const q = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today.getFullYear(), q * 3 + 3, 0);
      break;
    }

    case "previous_quarter": {
      let q = Math.floor(today.getMonth() / 3) - 1;
      let year = today.getFullYear();
      if (q < 0) {
        q = 3;
        year -= 1;
      }
      start = new Date(year, q * 3, 1);
      end = new Date(year, q * 3 + 3, 0);
      break;
    }

    default:
      return { start: "", end: "" };
  }

  // Format date as YYYY-MM-DD using local time zone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return { start: formatLocalDate(start), end: formatLocalDate(end) };
};

const LitigationDurationModal = ({ show, handleClose, currentDuration, onSave }) => {
  const [form, setForm] = useState({
    date_type: "lead_date",
    date_range_start: "",
    date_range_end: "",
    current_day: false,
    current_week: false,
    current_month: false,
    previous_month: false,
    current_quarter: false,
    previous_quarter: false,
    selected_years: [],
  });
  const [dateTypeDropdownOpen, setDateTypeDropdownOpen] = useState(false);
  const dateTypeRef = useRef(null);

  // Generate last 5 years
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (currentDuration) {
      setForm(currentDuration);
    }
  }, [currentDuration, show]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateTypeRef.current && !dateTypeRef.current.contains(event.target)) {
        setDateTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const update = (field, value) => {
    const resetPresets = PRESET_FIELDS.reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});

    setForm((f) => ({
      ...f,
      ...resetPresets,
      selected_years: [],
      [field]: value,
    }));
  };
  const toggleCheckbox = (field) => {
    const isActive = form[field];

    const resetPresets = PRESET_FIELDS.reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});

    if (isActive) {
      setForm((f) => ({
        ...f,
        ...resetPresets,
        date_range_start: "",
        date_range_end: "",
      }));
      return;
    }

    const { start, end } = getDateRangeFromPreset(field);

    setForm((f) => ({
      ...f,
      ...resetPresets,
      [field]: true,
      selected_years: [],
      date_range_start: start,
      date_range_end: end,
    }));
  };
  const toggleYear = (year) => {
    let updatedYears;

    if (form.selected_years.includes(year)) {
      updatedYears = form.selected_years.filter((y) => y !== year);
    } else {
      updatedYears = [...form.selected_years, year];
    }

    const resetPresets = PRESET_FIELDS.reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});

    if (updatedYears.length === 0) {
      setForm((f) => ({
        ...f,
        selected_years: [],
        date_range_start: "",
        date_range_end: "",
      }));
      return;
    }

    const minYear = Math.min(...updatedYears);
    const maxYear = Math.max(...updatedYears);

    setForm((f) => ({
      ...f,
      ...resetPresets,
      selected_years: updatedYears,
      date_range_start: `${minYear}-01-01`,
      date_range_end: `${maxYear}-12-31`,
    }));
  };

  const handleRecalculate = () => {
    onSave(form);
  };

  const handleClear = () => {
    setForm({
      date_type: "lead_date",
      date_range_start: "",
      date_range_end: "",
      current_day: false,
      current_week: false,
      current_month: false,
      previous_month: false,
      current_quarter: false,
      previous_quarter: false,
      selected_years: [],
    });
  };

  const selectedDateType = DATE_TYPE_OPTIONS.find((opt) => opt.id === form.date_type);

  const handleDateTypeSelect = (option) => {
    update("date_type", option.id);
    setDateTypeDropdownOpen(false);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
      size="md"
    >
      <div style={{ minHeight: "200px" }}>
        <Modal.Header
          style={{
            borderBottom: "0px",
          }}
          className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
        >
          <Modal.Title
            className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
            style={{ fontSize: "14px", fontWeight: "600" }}
          >
            Reporting Duration
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0 m-t-5 m-l-5 m-r-5 m-b-5" style={{ minHeight: "200px" }}>
          <div className="custom-tab">
            {/* date name dropdown */}
            <Row className="mx-0 pe-0 m-b-5">
              <Col md={12} className="p-0">
                <div
                  className="m-b-5"
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "var(--primary)",
                  }}
                >
                  Report Duration Calculated by:
                </div>
                <div
                  ref={dateTypeRef}
                  className="position-relative custom-select-new-provider"
                  style={{ width: "100%" }}
                >
                  <div
                    className="dropdown-button form-control pt-0 pb-0 pe-0 rounded-0 height-25 d-flex align-items-center"
                    onClick={() => setDateTypeDropdownOpen((prev) => !prev)}
                    style={{
                      paddingLeft: "5px",
                      cursor: "pointer",
                    }}
                  >
                    <span>{selectedDateType?.name || "Select Date Type"}</span>
                  </div>

                  {dateTypeDropdownOpen && (
                    <div
                      className="dropdown-menu p-0 mt-0"
                      style={{
                        display: "block",
                        position: "absolute",
                        width: "100%",
                        border: "1px solid #ced4da",
                        scrollbarWidth: "none",
                        maxHeight: "200px",
                        fontSize: "14px",
                        overflowY: "auto",
                        zIndex: 1000,
                        backgroundColor: "white",
                      }}
                    >
                      {DATE_TYPE_OPTIONS.map((option) => (
                        <div
                          key={option.id}
                          className="dropdown-option d-flex align-items-center height-25"
                          style={{
                            cursor: "pointer",
                            paddingLeft: "5px",
                            backgroundColor: form.date_type === option.id ? "#e9ecef" : "white",
                          }}
                          onClick={() => handleDateTypeSelect(option)}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor =
                              form.date_type === option.id ? "#e9ecef" : "white")
                          }
                        >
                          {option.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Custom Date Range Section */}
            <Row className="mx-0 pe-0 m-b-5">
              <Col md={12} className="p-0 ">
                <div
                  className="m-b-5"
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "var(--primary)",
                  }}
                >
                  Custom Date Range
                </div>
                <div className="row mx-0 align-items-center" style={{ marginBottom: "0px" }}>
                  <div className="col-md-6 height-25 p-l-0 p-r-5">
                    <input
                      type="date"
                      placeholder="From Date"
                      className="form-control height-25 p-0 p-l-5 rounded-0"
                      style={{ color: form.date_range_start ? "black" : "var(--primary-25)" }}
                      value={form.date_range_start}
                      onChange={(e) => update("date_range_start", e.target.value)}
                    />
                  </div>

                  <div className="col-md-6 height-25 p-l-0 pe-0">
                    <input
                      type="date"
                      placeholder="To Date"
                      className="form-control height-25 p-0 p-l-5 rounded-0"
                      style={{ color: form.date_range_end ? "black" : "var(--primary-25)" }}
                      value={form.date_range_end}
                      onChange={(e) => update("date_range_end", e.target.value)}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            {/* Quick Presets Section */}
            <Row className="mx-0 pe-0 m-b-5">
              <Col md={12} className="p-0">
                <div
                  className="m-b-5"
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "var(--primary)",
                  }}
                >
                  Quick Presets
                </div>

                {/* Row 1: Current Day, Current Week */}
                <div className="row mx-0 align-items-center" style={{ marginBottom: "5px" }}>
                  <div className="col-md-6 p-l-0 p-r-5">
                    <label
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.current_day}
                        onChange={() => toggleCheckbox("current_day")}
                        style={{
                          marginRight: "5px",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      Current Day
                    </label>
                  </div>

                  <div className="col-md-6 p-l-0 pe-0">
                    <label
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.current_week}
                        onChange={() => toggleCheckbox("current_week")}
                        style={{
                          marginRight: "5px",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      Current Week
                    </label>
                  </div>
                </div>

                {/* Row 2: Current Month, Previous Month */}
                <div className="row mx-0 align-items-center" style={{ marginBottom: "5px" }}>
                  <div className="col-md-6 p-l-0 p-r-5">
                    <label
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.current_month}
                        onChange={() => toggleCheckbox("current_month")}
                        style={{
                          marginRight: "5px",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      Current Month
                    </label>
                  </div>

                  <div className="col-md-6 p-l-0 pe-0">
                    <label
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.previous_month}
                        onChange={() => toggleCheckbox("previous_month")}
                        style={{
                          marginRight: "5px",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      Previous Month
                    </label>
                  </div>
                </div>

                {/* Row 3: Current Quarter, Previous Quarter */}
                <div className="row mx-0 align-items-center" style={{ marginBottom: "0px" }}>
                  <div className="col-md-6 p-l-0 p-r-5">
                    <label
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.current_quarter}
                        onChange={() => toggleCheckbox("current_quarter")}
                        style={{
                          marginRight: "5px",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      Current Quarter
                    </label>
                  </div>

                  <div className="col-md-6 p-l-0 pe-0">
                    <label
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.previous_quarter}
                        onChange={() => toggleCheckbox("previous_quarter")}
                        style={{
                          marginRight: "5px",
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      Previous Quarter
                    </label>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Years Section */}
            <Row className="mx-0 pe-0">
              <Col md={12} className="p-0">
                <div
                  className="m-b-5"
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "var(--primary)",
                  }}
                >
                  Select Years
                </div>

                <div
                  className="d-flex align-items-center"
                  style={{ gap: "10px", flexWrap: "wrap" }}
                >
                  {years.map((year) => (
                    <label
                      key={year}
                      className="d-flex align-items-center"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.selected_years.includes(year)}
                        onChange={() => toggleYear(year)}
                        style={{
                          marginRight: "5px",
                          width: "13px",
                          height: "13px",
                          cursor: "pointer",
                        }}
                      />
                      {year}
                    </label>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>

        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ borderTop: "none" }}
        >
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            variant="primary"
            onClick={handleClear}
          >
            Clear All
          </Button>
          <Button
            variant="success"
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            onClick={handleRecalculate}
          >
            Recalculate
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default LitigationDurationModal;
